import { useEffect, useState } from 'react';
import { Domain, } from '@repo/domain';
import { useQueryClient } from '@tanstack/react-query';
import { ownedParkingKeys } from '../../constants/ownedParkings';
import { resolveRepository } from '../../registry/resolveRepository';
import { useOwnedParkings } from './useOwnedParkings';
const UPLOADED_RECHECK_INTERVAL_MILLISECONDS = 2000;
const findFirstUploadedImageLocation = (ownedParkings) => {
    for (const parking of ownedParkings) {
        if (parking.images.some((image) => image.status === 'uploaded')) {
            return { parkingId: parking.id, group: 'parkingOnly' };
        }
        for (const spot of parking.spots) {
            if (spot.images.some((image) => image.status === 'uploaded')) {
                return { parkingId: parking.id, group: 'parkingSpot' };
            }
        }
    }
    return undefined;
};
const replaceParkingOnlyImages = (ownedParkings, parkingId, freshImages) => ownedParkings.map((parking) => (parking.id === parkingId ? { ...parking, images: freshImages } : parking));
const replaceParkingSpotImages = (ownedParkings, parkingId, freshImages) => ownedParkings.map((parking) => {
    if (parking.id !== parkingId)
        return parking;
    const imagesBySpotId = new Map();
    for (const image of freshImages) {
        const bucket = imagesBySpotId.get(image.relatedTo.id);
        if (bucket) {
            bucket.push(image);
        }
        else {
            imagesBySpotId.set(image.relatedTo.id, [image]);
        }
    }
    return {
        ...parking,
        spots: parking.spots.map((spot) => ({ ...spot, images: imagesBySpotId.get(spot.id) ?? [] })),
    };
});
export function useOwnedParkingsImagesLive(filter, scope) {
    const baseResult = useOwnedParkings(filter, scope);
    const queryClient = useQueryClient();
    const { ownedParkings } = baseResult;
    const [rescanTick, setRescanTick] = useState(0);
    useEffect(() => {
        const location = findFirstUploadedImageLocation(ownedParkings);
        if (!location)
            return;
        let cancelled = false;
        const timer = setTimeout(async () => {
            const repository = await resolveRepository(Domain.OwnedParking);
            const freshImages = location.group === 'parkingOnly'
                ? await repository.getParkingOnlyImages(location.parkingId)
                : await repository.getParkingSpotImages(location.parkingId);
            if (cancelled)
                return;
            queryClient.setQueriesData({ queryKey: ownedParkingKeys.all }, (previous) => {
                if (!previous)
                    return previous;
                return location.group === 'parkingOnly'
                    ? replaceParkingOnlyImages(previous, location.parkingId, freshImages)
                    : replaceParkingSpotImages(previous, location.parkingId, freshImages);
            });
            // TanStack's structural sharing keeps the old reference when the refreshed payload is
            // deeply equal to the previous one (still UPLOADED), so the consumer hook does not
            // re-emit. Bumping a local tick forces this effect to re-run and reschedule the next
            // check regardless of structural equality.
            setRescanTick((previous) => previous + 1);
        }, UPLOADED_RECHECK_INTERVAL_MILLISECONDS);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [ownedParkings, rescanTick, queryClient]);
    return baseResult;
}
